/**
 * Delivery Photo Verification Component
 * Shows sender's package photos and driver verification photos
 * With NEW badge for 120 days, auto-archive after 90 days
 */
import { useState, useEffect } from 'react';
import { Camera, Eye, Archive, Clock, AlertCircle, X, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const DeliveryPhotoVerification = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    fetchDeliveries();
  }, [includeArchived]);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/photos/my-deliveries?include_archived=${includeArchived}`);
      setDeliveries(response.data.deliveries || []);
      setPolicy(response.data.policy);
    } catch (error) {
      console.error('Failed to fetch delivery photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTotalPhotos = (delivery) => {
    return (delivery.sender_photos?.length || 0) + 
           (delivery.verification_photos?.length || 0) + 
           (delivery.delivery_proof ? 1 : 0);
  };

  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-800 rounded w-1/3"></div>
          <div className="h-24 bg-slate-800 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
      {/* Header with NEW Banner */}
      <div className="relative bg-gradient-to-r from-emerald-600/20 via-yellow-500/20 to-emerald-600/20 border-b border-emerald-500/30 p-4">
        <div className="absolute top-0 right-0 bg-gradient-to-l from-yellow-500 to-amber-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          NEW FEATURE
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Camera className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Delivery Photo Verification</h3>
            <p className="text-sm text-slate-400">View photos for dispute resolution</p>
          </div>
        </div>
      </div>

      {/* Policy Banner */}
      {policy && (
        <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-700 flex items-center gap-2 text-xs text-slate-400">
          <Clock className="w-3 h-3" />
          <span>Photos archived after {policy.archive_after_days} days • Deleted after {policy.delete_after_days} days</span>
        </div>
      )}

      {/* Controls */}
      <div className="p-4 border-b border-slate-800">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
          />
          <span className="text-sm text-slate-400">Show archived photos (older than 90 days)</span>
        </label>
      </div>

      {/* Deliveries List */}
      <div className="divide-y divide-slate-800 max-h-96 overflow-y-auto">
        {deliveries.length === 0 ? (
          <div className="p-8 text-center">
            <Camera className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No delivery photos found</p>
            <p className="text-sm text-slate-500 mt-1">Photos will appear here after you send packages</p>
          </div>
        ) : (
          deliveries.map((delivery) => (
            <div
              key={delivery.package_id}
              className="p-4 hover:bg-slate-800/50 transition-colors cursor-pointer"
              onClick={() => setSelectedDelivery(delivery)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    delivery.is_new ? 'bg-emerald-500/20' : 'bg-slate-700'
                  }`}>
                    <Camera className={`w-5 h-5 ${delivery.is_new ? 'text-emerald-400' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{delivery.tracking_id}</span>
                      {delivery.is_new && (
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30">
                          NEW
                        </span>
                      )}
                      {delivery.age_days > 90 && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full border border-amber-500/30 flex items-center gap-1">
                          <Archive className="w-3 h-3" />
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <span>To: {delivery.recipient_name}</span>
                      <span>•</span>
                      <span>{formatDate(delivery.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm text-white">{getTotalPhotos(delivery)} photos</div>
                    {delivery.days_until_archive > 0 && delivery.days_until_archive <= 30 && (
                      <div className="text-xs text-amber-400">
                        Archives in {delivery.days_until_archive} days
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Photo Modal */}
      {selectedDelivery && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <div>
                <h3 className="font-semibold text-white">{selectedDelivery.tracking_id}</h3>
                <p className="text-sm text-slate-400">To: {selectedDelivery.recipient_name}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDelivery(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Sender Photos */}
              {selectedDelivery.sender_photos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Package Photos (Uploaded at Checkout)
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedDelivery.sender_photos.map((photo, idx) => (
                      <a
                        key={idx}
                        href={photo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-square rounded-lg overflow-hidden bg-slate-800 hover:ring-2 hover:ring-emerald-500 transition-all"
                      >
                        <img src={photo} alt={`Package ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Driver Verification Photos */}
              {selectedDelivery.verification_photos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Driver Verification Photos
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedDelivery.verification_photos.map((photo, idx) => (
                      <a
                        key={idx}
                        href={photo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-square rounded-lg overflow-hidden bg-slate-800 hover:ring-2 hover:ring-blue-500 transition-all"
                      >
                        <img src={photo} alt={`Verification ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Delivery Proof */}
              {selectedDelivery.delivery_proof && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Delivery Proof Photo
                  </h4>
                  <a
                    href={selectedDelivery.delivery_proof}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-48 aspect-square rounded-lg overflow-hidden bg-slate-800 hover:ring-2 hover:ring-green-500 transition-all"
                  >
                    <img src={selectedDelivery.delivery_proof} alt="Delivery Proof" className="w-full h-full object-cover" />
                  </a>
                </div>
              )}

              {/* No Photos */}
              {getTotalPhotos(selectedDelivery) === 0 && (
                <div className="text-center py-8">
                  <Camera className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No photos available for this delivery</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryPhotoVerification;
